/**
 * navigation.js — Iter249
 * ═══════════════════════════════════════════════════════════════════════════
 * Helper standardisé pour `navigate()` avec transmission du contexte de retour.
 *
 * USAGE :
 *   import { navigateWithBack } from '../utils/navigation';
 *   const navigate = useNavigate();
 *   const location = useLocation();
 *
 *   // Remplace : navigate('/my-company-space')
 *   // Par :     navigateWithBack(navigate, location, '/my-company-space')
 *
 * BÉNÉFICES :
 *   - `useSmartBack` reçoit un `location.state.from` cohérent → BackButton
 *     retourne au contexte précédent (et non au fallback par défaut).
 *   - Évite les `navigate(target, { state: { from: location.pathname } })`
 *     éparpillés dans 60+ appels.
 *
 * API :
 *   navigateWithBack(navigate, location, to, options?)
 *     - navigate : la fonction `useNavigate()`
 *     - location : l'objet `useLocation()`
 *     - to       : la route cible (string ou { pathname, search, ... })
 *     - options  : { replace?, state?, ... } — state fusionné avec { from }
 *
 *   navigateExternalReturn(navigate, to, fromOverride)
 *     - Utile quand on souhaite forcer un `from` différent (ex: deep-link
 *       externe où `location.pathname` n'est pas pertinent).
 */

/**
 * Navigate vers `to` en injectant `state.from` = location.pathname courant.
 */
export function navigateWithBack(navigate, location, to, options = {}) {
  if (!navigate || typeof navigate !== 'function') {
    throw new Error('[navigateWithBack] navigate function is required');
  }
  const currentPath = (location && location.pathname) || null;
  const mergedState = {
    ...(options.state || {}),
    from: (options.state && options.state.from) || currentPath,
  };
  navigate(to, { ...options, state: mergedState });
}

/**
 * Variante explicite — forcer un `from` précis (deep-link, post-login, etc.).
 */
export function navigateExternalReturn(navigate, to, fromOverride, options = {}) {
  if (!navigate || typeof navigate !== 'function') {
    throw new Error('[navigateExternalReturn] navigate function is required');
  }
  const mergedState = {
    ...(options.state || {}),
    from: fromOverride || null,
  };
  navigate(to, { ...options, state: mergedState });
}
